import React from 'react';
import { flowRight } from 'lodash/fp';
import { withSnackbar } from 'notistack';

import StepsForm from 'ui/form/StepsForm';
import { getCountryRegions } from 'utils/county-data';

import LoaderStep from './steps/LoaderStep';
import CompanyIntegrationDetailsStep from './steps/CompanyIntegrationDetailsStep';
import CompanyIntegrationAccountStep from './steps/CompanyIntegrationAccountStep';
import { loadCompany, createCompany, validateAccount } from './services';
import * as valid from 'utils/validators';
import { withAppContext } from '../../AppContext';
import { isCustomerType } from './consts';
import integrationConfigs from '../../integrations/types/configs';
import { loadServices } from '../../integrations/services';
import { addCompany } from '../../Services';

const LOADER_STEP = 'loader';
const DETAIL_STEP = 'detail';
const ACCOUNT_STEP = 'account';

const INITIAL_VALUES = {
    business_name: '',
    address1: '',
    address2: '',
    city: '',
    country: 'US',
    region: 'AL',
    postal_code: '',
    name: '',
    logo: '',
    type: '',
    first_name: '',
    last_name: '',
    email: '',
    phone: '',
    license_type: 'billable_this_month',
    subscription_level: 'advanced',
    seats: '',
    externalCompanies: [],
    agreement: null,
    licenseAck: false,
    createAppAccount: true,
    createPartnerAccount: false,
};

const validateAccountField = fieldName => async (values, errors) => {
    let result = null;
    if (!errors[fieldName]) {
        const exists = await validateAccount({
            [fieldName]: values[fieldName],
        });
        if (exists) {
            result = [fieldName, 'exists'];
        }
    }
    return result;
};

const isNotCustomer = values => !isCustomerType(values.type);

const findRegion = (regions, region) =>
    regions && !regions[region] ? Object.keys(regions)[0] : region;

const STEPS = {
    [LOADER_STEP]: {
        component: LoaderStep,
        update: async ({ goNext, setInitValues, extra }) => {
            const services = await loadServices();
            const config = integrationConfigs.find(
                item => item.name === extra.params.type
            );
            const service = services.find(
                item => item.identifier === extra.params.type
            );
            const { lookups } = await config.serviceProvider.companies(service);
            const data = await loadCompany(extra.params.id);
            const country = data.country;
            const regions = getCountryRegions(country);
            const region = findRegion(regions, data.region);
            setInitValues({
                ...INITIAL_VALUES,
                country,
                region,
                service,
                config,
                lookups,
            });
            goNext();
        },
        next: DETAIL_STEP,
    },
    [DETAIL_STEP]: {
        label: 'Company Details',
        component: CompanyIntegrationDetailsStep,
        validate: valid.rules([
            valid.notEmpty('type'),
            valid.notEmpty('business_name'),
            valid.notEmpty('name'),
            valid.conditionCheck(
                isNotCustomer,
                valid.rules([
                    valid.notEmpty('address1'),
                    valid.notEmpty('city'),
                    valid.notEmpty('country'),
                    valid.notEmpty('region'),
                    valid.notEmpty('postal_code'),
                ])
            ),
        ]),
        next: async ({ setNext, values, onChange }) => {
            const getCompanyByName = name =>
                values.externalCompanies.find(item => item.label === name);
            const { serviceProvider } = values.config;
            const primaryContact = await serviceProvider.companyPrimaryContact(
                values.service,
                getCompanyByName(values.business_name).value
            );
            onChange({ ...primaryContact });
            setNext(ACCOUNT_STEP);
        },
        back: ({ redirectTo }) => redirectTo('/companies'),
    },
    [ACCOUNT_STEP]: {
        label: 'Create account',
        component: CompanyIntegrationAccountStep,
        validate: valid.rules([
            valid.notEmpty('first_name'),
            valid.notEmpty('last_name'),
            valid.notEmpty('email'),
            valid.emailType('email'),
            validateAccountField('email'),
            valid.notEmpty('license_type'),
            valid.notEmpty('subscription_level'),
            valid.notEmptyNumber('seats'),
            valid.notEmpty('licenseAck'),
            valid.notEmpty('agreement'),
            valid.conditionCheck(
                isNotCustomer,
                valid.rules([
                    valid.notEmpty('username'),
                    validateAccountField('username'),
                ])
            ),
        ]),
        next: async ({ onSave, values, redirectTo }) => {
            const getCompanyByName = name =>
                values.externalCompanies.find(item => item.label === name);
            values.mapping = {
                external_organization_identifier: getCompanyByName(
                    values.business_name
                ).value,
                external_organization_name: values.business_name,
                external_agreement_identifier: values.agreement.value,
                external_agreement_name: values.agreement.label,
            };
            values['connector_uuid'] = values.service.uuid;
            const url = await onSave(values);
            redirectTo(url);
        },
        back: DETAIL_STEP,
    },
};

const handleSave = (app, notify) => async data => {
    const uid = await addCompany(app.currentUser.uuid, data);
    notify('Organization created');
    return `/companies/${uid}`;
};

let CreateFromIntergation = ({ app, enqueueSnackbar, match: { params } }) => {
    return (
        <StepsForm
            steps={STEPS}
            first={LOADER_STEP}
            onSave={handleSave(app, enqueueSnackbar)}
            extra={{
                user: app.currentUser,
                creating: true,
                params,
            }}
        />
    );
};

export default flowRight(withAppContext, withSnackbar)(CreateFromIntergation);
